/* ==========================================================================
   SMILE BOXX — OWN A BOXX
   ==========================================================================
   Four jobs, in order of how much they matter:

     1. FAQ accordion with a measured height animation
     2. Sticky masthead state once the hero is behind you
     3. Smooth anchor scrolling that clears the sticky masthead
     4. The enquiry form -> GoHighLevel
     5. Dev-only ?terms= / ?cta= mode preview  (NOT ported to Shopify)

   Everything is scoped to a root element rather than the document, and every
   listener hangs off one AbortController, so the theme editor can re-run init()
   on a section reload without stacking duplicate handlers.

   The earlier version of this page reused the representative page's script.
   That stopped being sensible once this page stopped sharing its structure —
   there is no longer a single selector in common.
   ========================================================================== */
(() => {
  const ROOT_SELECTOR = "[data-own-a-boxx-page]";
  const instances = new WeakMap();

  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
  const canAnimate = () =>
    !reducedMotion.matches && typeof Element.prototype.animate === "function";

  function init(root = document.querySelector(ROOT_SELECTOR)) {
    if (!root) return;

    // Tear down a previous run on this root before rebinding.
    instances.get(root)?.abort();
    const controller = new AbortController();
    instances.set(root, controller);
    const { signal } = controller;

    const year = root.querySelector("#year");
    if (year) year.textContent = String(new Date().getFullYear());

    bindFaq(root, signal);
    bindMasthead(root, signal);
    bindAnchors(root, signal);
    bindEnquiryForm(root, signal);
    applyModePreview(root);
  }

  /* ---------------------------------------------------------------- FAQ -- */
  function bindFaq(root, signal) {
    const items = [...root.querySelectorAll(".faq details")];
    const running = new WeakMap();

    const clear = (item) => {
      item.classList.remove("is-animating");
      item.style.removeProperty("height");
      item.style.removeProperty("overflow");
    };

    items.forEach((item) => {
      const summary = item.querySelector("summary");
      if (!summary) return;

      summary.addEventListener(
        "click",
        (event) => {
          if (!canAnimate()) return; // let the browser do it natively

          event.preventDefault();
          running.get(item)?.cancel();

          const startHeight = item.offsetHeight;
          const summaryHeight = summary.offsetHeight;

          if (item.open) {
            // Closing: animate to the summary height, then actually close.
            item.classList.add("is-animating");
            const anim = item.animate(
              { height: [`${startHeight}px`, `${summaryHeight}px`] },
              { duration: 260, easing: "cubic-bezier(0.22, 1, 0.36, 1)" }
            );
            running.set(item, anim);
            anim.onfinish = () => {
              item.open = false;
              clear(item);
              running.delete(item);
            };
            anim.oncancel = () => clear(item);
          } else {
            // Opening: open first so the full height is measurable.
            item.open = true;
            item.classList.add("is-animating");
            const endHeight = item.offsetHeight;
            const anim = item.animate(
              { height: [`${summaryHeight}px`, `${endHeight}px`] },
              { duration: 300, easing: "cubic-bezier(0.22, 1, 0.36, 1)" }
            );
            running.set(item, anim);
            anim.onfinish = () => {
              clear(item);
              running.delete(item);
            };
            anim.oncancel = () => clear(item);
          }
        },
        { signal }
      );
    });
  }

  /* ----------------------------------------------------------- MASTHEAD -- */
  function bindMasthead(root, signal) {
    // The masthead sits transparent over the hero photograph and only gains a
    // background once there is content behind it. A class on the root rather
    // than an attribute on <html>, so the Shopify section stays self-contained.
    const setState = () => {
      root.classList.toggle("is-scrolled", window.scrollY > 40);
    };
    setState();
    window.addEventListener("scroll", setState, { passive: true, signal });
  }

  /* ------------------------------------------------------------ ANCHORS -- */
  function bindAnchors(root, signal) {
    root.querySelectorAll('a[href^="#"]:not(.skip-link)').forEach((link) => {
      link.addEventListener(
        "click",
        (event) => {
          const id = link.getAttribute("href");
          if (!id || id === "#") return;

          const target = root.querySelector(id) || document.querySelector(id);
          if (!target) return;

          event.preventDefault();
          const masthead = root.querySelector(".masthead");
          const offset = (masthead?.offsetHeight ?? 0) + 12;
          const top = target.getBoundingClientRect().top + window.scrollY - offset;

          window.scrollTo({
            top,
            behavior: reducedMotion.matches ? "auto" : "smooth",
          });
        },
        { signal }
      );
    });
  }


  /* ------------------------------------------------------------- LISTBOX -- */
  /* A real listbox, replacing every <select> on this page.

     WHY. The background field used to be a native <select> with a chevron set as
     a background-image. Windows Chrome draws the open popup using the control's
     own background properties, so the chevron TILED down the option list as
     repeating pink zigzags on the dark fill - the "broken dropdown" the client
     reported. A <select> popup is drawn by the OS and cannot be styled, so on a
     dark form its appearance is a coin flip per platform.

     This also earns its keep on the phone field, where a 200-country list needs
     a search box that a <select> cannot provide.

     Keyboard contract: Enter/Space/Down opens, Up/Down move, Home/End jump,
     Enter/Space choose, Escape closes and returns focus, Tab closes. Printable
     keys type-ahead when there is no search box. */
  function buildListbox(root, host, signal) {
    const name = host.dataset.name;
    const searchable = "searchable" in host.dataset;
    const placeholder = host.dataset.placeholder || "";
    const listId = `cs-${name}-list-${Math.random().toString(36).slice(2, 7)}`;

    host.innerHTML = `
      <button type="button" class="cselect-btn" aria-haspopup="listbox" aria-expanded="false"
              aria-controls="${listId}">
        <span class="cselect-value"></span>
        <svg class="cselect-chev" viewBox="0 0 14 9" width="12" height="8" aria-hidden="true"
             fill="none" stroke="currentColor" stroke-width="2"
             stroke-linecap="round" stroke-linejoin="round"><path d="M1 1l6 6 6-6"/></svg>
      </button>
      <div class="cselect-pop" hidden>
        ${searchable ? `<input type="text" class="cselect-search" autocomplete="off" spellcheck="false"
                              placeholder="${host.dataset.searchPlaceholder || "Search"}" />` : ""}
        <ul class="cselect-list" id="${listId}" role="listbox"></ul>
      </div>`;

    const btn = host.querySelector(".cselect-btn");
    const valueEl = host.querySelector(".cselect-value");
    const pop = host.querySelector(".cselect-pop");
    const list = host.querySelector(".cselect-list");
    const search = host.querySelector(".cselect-search");
    const hidden = document.createElement("input");
    hidden.type = "hidden";
    hidden.name = name;
    host.appendChild(hidden);

    if (host.dataset.labelledby) btn.setAttribute("aria-labelledby", `${host.dataset.labelledby} ${listId}-val`);
    valueEl.id = `${listId}-val`;
    if (host.dataset.describedby) btn.setAttribute("aria-describedby", host.dataset.describedby);

    let items = [];      // {value, label, html, search}
    let shown = [];
    let active = -1;
    let typed = "";
    let typedAt = 0;

    const api = {
      host, get value() { return hidden.value; },
      set(v, silent) {
        const it = items.find((i) => i.value === v);
        hidden.value = it ? it.value : "";
        valueEl.textContent = it ? it.short || it.label : placeholder;
        valueEl.toggleAttribute("data-placeholder", !it);
        if (!silent) host.dispatchEvent(new CustomEvent("cselect:change", { bubbles: true }));
      },
      setItems(next) { items = next; render(""); api.set(hidden.value, true); },
      focus() { btn.focus(); },
    };

    function render(q) {
      const needle = q.trim().toLowerCase();
      shown = needle ? items.filter((i) => i.search.includes(needle)) : items.slice();
      if (!shown.length) {
        list.innerHTML = `<li class="cselect-empty" role="presentation">No matches</li>`;
        active = -1;
        return;
      }
      list.innerHTML = shown
        .map((i, n) => `<li role="option" id="${listId}-o${n}" data-value="${escapeAttr(i.value)}"
              aria-selected="${i.value === hidden.value}">${i.html || escapeHtml(i.label)}</li>`)
        .join("");
      setActive(Math.max(0, shown.findIndex((i) => i.value === hidden.value)));
    }

    function setActive(n) {
      active = n;
      [...list.children].forEach((li, i) => li.toggleAttribute("data-active", i === n));
      const el = list.children[n];
      if (el) {
        list.setAttribute("aria-activedescendant", el.id);
        el.scrollIntoView({ block: "nearest" });
      }
    }

    function open() {
      if (!host.hasAttribute("data-open")) {
        // Only one listbox open at a time, or the phone picker and the
        // background picker can overlap each other.
        document.querySelectorAll("[data-cselect][data-open]").forEach((other) => {
          if (other !== host) other.dispatchEvent(new CustomEvent("cselect:close"));
        });
        host.setAttribute("data-open", "");
        pop.hidden = false;
        btn.setAttribute("aria-expanded", "true");
        // Flip upward when the popup would run off the bottom of the viewport.
        const room = window.innerHeight - btn.getBoundingClientRect().bottom;
        pop.toggleAttribute("data-drop", room < 300);
        if (room < 300) pop.setAttribute("data-drop", "up");
        render(search ? search.value : "");
        if (search) { search.value = ""; render(""); search.focus(); }
      }
    }

    function close(refocus) {
      if (host.hasAttribute("data-open")) {
        host.removeAttribute("data-open");
        pop.hidden = true;
        btn.setAttribute("aria-expanded", "false");
        if (refocus) btn.focus();
      }
    }

    function choose(n) {
      const it = shown[n];
      if (!it) return;
      api.set(it.value);
      close(true);
    }

    btn.addEventListener("click", () => (host.hasAttribute("data-open") ? close(true) : open()), { signal });

    list.addEventListener("mousedown", (e) => {
      const li = e.target.closest("li[role='option']");
      if (!li) return;
      e.preventDefault();
      choose([...list.children].indexOf(li));
    }, { signal });

    list.addEventListener("mousemove", (e) => {
      const li = e.target.closest("li[role='option']");
      if (li) setActive([...list.children].indexOf(li));
    }, { signal });

    if (search) search.addEventListener("input", () => render(search.value), { signal });

    host.addEventListener("keydown", (e) => {
      const isOpen = host.hasAttribute("data-open");
      switch (e.key) {
        case "ArrowDown": e.preventDefault(); isOpen ? setActive(Math.min(active + 1, shown.length - 1)) : open(); return;
        case "ArrowUp": e.preventDefault(); isOpen ? setActive(Math.max(active - 1, 0)) : open(); return;
        case "Home": if (isOpen) { e.preventDefault(); setActive(0); } return;
        case "End": if (isOpen) { e.preventDefault(); setActive(shown.length - 1); } return;
        case "Enter": if (isOpen) { e.preventDefault(); choose(active); } else if (e.target === btn) { e.preventDefault(); open(); } return;
        case " ":
          if (e.target === search) return;           // a space is a real character in the search box
          e.preventDefault(); isOpen ? choose(active) : open(); return;
        case "Escape": if (isOpen) { e.preventDefault(); close(true); } return;
        case "Tab": close(false); return;
        default: break;
      }
      // Type-ahead, only where there is no search box to type into.
      if (!search && e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) {
        const now = Date.now();
        typed = now - typedAt > 900 ? e.key : typed + e.key;
        typedAt = now;
        const hit = shown.findIndex((i) => i.search.startsWith(typed.toLowerCase()));
        if (hit > -1) { open(); setActive(hit); }
      }
    }, { signal });

    document.addEventListener("pointerdown", (e) => {
      if (!host.contains(e.target)) close(false);
    }, { signal });

    host.addEventListener("cselect:close", () => close(false), { signal });

    if (host.dataset.options) {
      api.setItems(host.dataset.options.split("|").map((o) => ({
        value: o, label: o, search: o.toLowerCase(),
      })));
    } else {
      api.set("", true);
    }
    return api;
  }

  const escapeHtml = (v) => String(v).replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
  const escapeAttr = escapeHtml;

  /* ------------------------------------------------------------ COUNTRIES -- */
  /* name:iso2:dial. The flag is derived from the ISO code rather than stored, so
     each entry stays short. US and CA are pinned to the top - the territories
     being sold are North American - and the rest follow alphabetically. */
  const DIAL_DATA =
    "United States:US:1|Canada:CA:1|United Kingdom:GB:44|Australia:AU:61|Ireland:IE:353|" +
    "Afghanistan:AF:93|Albania:AL:355|Algeria:DZ:213|Andorra:AD:376|Angola:AO:244|Argentina:AR:54|" +
    "Armenia:AM:374|Austria:AT:43|Azerbaijan:AZ:994|Bahamas:BS:1242|Bahrain:BH:973|Bangladesh:BD:880|" +
    "Barbados:BB:1246|Belarus:BY:375|Belgium:BE:32|Belize:BZ:501|Benin:BJ:229|Bermuda:BM:1441|" +
    "Bolivia:BO:591|Bosnia and Herzegovina:BA:387|Botswana:BW:267|Brazil:BR:55|Brunei:BN:673|" +
    "Bulgaria:BG:359|Burkina Faso:BF:226|Cambodia:KH:855|Cameroon:CM:237|Cape Verde:CV:238|" +
    "Chile:CL:56|China:CN:86|Colombia:CO:57|Costa Rica:CR:506|Croatia:HR:385|Cyprus:CY:357|" +
    "Czechia:CZ:420|Denmark:DK:45|Dominican Republic:DO:1809|Ecuador:EC:593|Egypt:EG:20|" +
    "El Salvador:SV:503|Estonia:EE:372|Ethiopia:ET:251|Fiji:FJ:679|Finland:FI:358|France:FR:33|" +
    "Georgia:GE:995|Germany:DE:49|Ghana:GH:233|Greece:GR:30|Guatemala:GT:502|Honduras:HN:504|" +
    "Hong Kong:HK:852|Hungary:HU:36|Iceland:IS:354|India:IN:91|Indonesia:ID:62|Iraq:IQ:964|" +
    "Israel:IL:972|Italy:IT:39|Jamaica:JM:1876|Japan:JP:81|Jordan:JO:962|Kazakhstan:KZ:7|" +
    "Kenya:KE:254|Kuwait:KW:965|Latvia:LV:371|Lebanon:LB:961|Libya:LY:218|Lithuania:LT:370|" +
    "Luxembourg:LU:352|Malaysia:MY:60|Maldives:MV:960|Malta:MT:356|Mauritius:MU:230|Mexico:MX:52|" +
    "Moldova:MD:373|Monaco:MC:377|Mongolia:MN:976|Montenegro:ME:382|Morocco:MA:212|Nepal:NP:977|" +
    "Netherlands:NL:31|New Zealand:NZ:64|Nicaragua:NI:505|Nigeria:NG:234|North Macedonia:MK:389|" +
    "Norway:NO:47|Oman:OM:968|Pakistan:PK:92|Panama:PA:507|Paraguay:PY:595|Peru:PE:51|" +
    "Philippines:PH:63|Poland:PL:48|Portugal:PT:351|Puerto Rico:PR:1787|Qatar:QA:974|Romania:RO:40|" +
    "Rwanda:RW:250|Saudi Arabia:SA:966|Senegal:SN:221|Serbia:RS:381|Singapore:SG:65|Slovakia:SK:421|" +
    "Slovenia:SI:386|South Africa:ZA:27|South Korea:KR:82|Spain:ES:34|Sri Lanka:LK:94|Sweden:SE:46|" +
    "Switzerland:CH:41|Taiwan:TW:886|Tanzania:TZ:255|Thailand:TH:66|Trinidad and Tobago:TT:1868|" +
    "Tunisia:TN:216|Turkey:TR:90|Uganda:UG:256|Ukraine:UA:380|United Arab Emirates:AE:971|" +
    "Uruguay:UY:598|Uzbekistan:UZ:998|Venezuela:VE:58|Vietnam:VN:84|Zambia:ZM:260|Zimbabwe:ZW:263";

  const flagOf = (iso) =>
    String.fromCodePoint(...[...iso].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65));

  const COUNTRIES = DIAL_DATA.split("|").map((row) => {
    const [name, iso, dial] = row.split(":");
    return {
      value: iso,
      label: `${name} +${dial}`,
      short: `${flagOf(iso)} +${dial}`,
      dial,
      html: `<span class="flag">${flagOf(iso)}</span><span class="cname">${escapeHtml(name)}</span>` +
            `<span class="dial">+${dial}</span>`,
      search: `${name} +${dial} ${iso}`.toLowerCase(),
    };
  });

  /* --------------------------------------------------------- ENQUIRY FORM -- */
  /* Custom-coded form, submitted to GoHighLevel through a Workflow Inbound
     Webhook whose URL sits in data-endpoint on the <form>.

     WHY fetch() AND NOT A NATIVE POST
     A native cross-origin POST would navigate the visitor away to a bare GHL
     response. fetch() keeps them here and lets the page own the confirmation.

     CORS was checked directly on 22 Aug 2026: the endpoint returns
     Access-Control-Allow-Origin: * and allows POST, so a normal fetch works and
     the real status is readable. Do NOT switch to mode:"no-cors" - it makes
     every response opaque, so failures would be reported as successes.

     UNCONFIGURED IS NOT SILENT. With data-endpoint empty the form refuses to
     submit and says so. A form that appears to send and does not is the worst of
     the three states. */
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  function bindEnquiryForm(root, signal) {
    const form = root.querySelector(".enquiry-form");
    if (!form) return;

    const wrap = form.closest(".enquire");
    const done = wrap?.querySelector(".form-done");
    const status = form.querySelector(".form-status");
    const selects = {};
    form.querySelectorAll("[data-cselect]").forEach((host) => {
      selects[host.dataset.name] = buildListbox(root, host, signal);
    });
    if (selects.dial) {
      selects.dial.setItems(COUNTRIES);
      selects.dial.set("US", true);
    }

    const F = {
      name: form.querySelector("#oab-name"),
      email: form.querySelector("#oab-email"),
      phone: form.querySelector("#oab-phone"),
      territory: form.querySelector("#oab-territory"),
      notes: form.querySelector("#oab-notes"),
      company: form.querySelector("#oab-company"),
    };
    let sending = false;

    /* One rule per field, so validation and messaging never drift apart. */
    const RULES = [
      { key: "name", el: () => F.name, err: "err-name",
        test: (v) => v.trim().length >= 2,
        msg: "Please add your name so we know who to reply to." },
      { key: "email", el: () => F.email, err: "err-email",
        test: (v) => EMAIL_RE.test(v.trim()),
        msg: "Please enter a valid email address." },
      { key: "phone", el: () => F.phone, err: "err-phone",
        // Digits only, 6-15, matching the ITU range. A local number never
        // includes the country code - that is what the picker is for.
        test: (v) => { const d = v.replace(/\D/g, ""); return d.length >= 6 && d.length <= 15; },
        msg: "Please enter a phone number — digits only, without the country code." },
      { key: "territory", el: () => F.territory, err: "err-territory",
        test: (v) => v.trim().length >= 2,
        msg: "Which market are you asking about? A city, state or region is enough." },
      { key: "background", el: () => selects.background?.host, err: "err-background",
        value: () => selects.background?.value || "",
        test: (v) => !!v,
        msg: "Please choose the option that best describes you." },
    ];

    const errEl = (id) => form.querySelector(`#${id}`);

    function showError(rule, show) {
      const box = errEl(rule.err);
      const el = rule.el();
      if (box) {
        box.textContent = show ? rule.msg : "";
        box.hidden = !show;
      }
      if (el) el.setAttribute("aria-invalid", show ? "true" : "false");
    }

    function valueOf(rule) {
      return rule.value ? rule.value() : (rule.el()?.value ?? "");
    }

    function check(rule, quiet) {
      const ok = rule.test(valueOf(rule));
      if (!quiet) showError(rule, !ok);
      return ok;
    }

    // Validate on blur, then live once a field has already complained - so a
    // person fixing a mistake sees it clear the moment it is right, but nobody
    // is scolded for a half-typed email.
    RULES.forEach((rule) => {
      const el = rule.el();
      if (!el) return;
      const revalidate = () => { if (errEl(rule.err)?.hidden === false) check(rule); };
      if (el.matches("input, textarea")) {
        el.addEventListener("blur", () => { if (el.value.trim()) check(rule); }, { signal });
        el.addEventListener("input", revalidate, { signal });
      } else {
        el.addEventListener("cselect:change", revalidate, { signal });
      }
    });

    // Phone: keep it to characters that can belong to a number.
    F.phone?.addEventListener("input", () => {
      const cleaned = F.phone.value.replace(/[^\d\s()\-.]/g, "");
      if (cleaned !== F.phone.value) F.phone.value = cleaned;
    }, { signal });

    const say = (message, tone) => {
      if (!status) return;
      status.textContent = message;
      status.dataset.tone = tone;
      status.hidden = false;
    };

    form.addEventListener("submit", async (event) => {
      event.preventDefault();
      if (sending) return;

      // Honeypot: report success, send nothing.
      if (F.company && F.company.value.trim()) {
        showDone("done", "", "");
        return;
      }

      const bad = RULES.filter((r) => !check(r));
      if (bad.length) {
        say(`Please check ${bad.length === 1 ? "the highlighted field" : "the highlighted fields"}.`, "error");
        const first = bad[0].el();
        (first?.matches("input, textarea") ? first : first?.querySelector(".cselect-btn"))?.focus();
        first?.scrollIntoView({ block: "center", behavior: "smooth" });
        return;
      }
      if (status) status.hidden = true;

      const endpoint = (form.dataset.endpoint || "").trim();
      if (!endpoint) {
        say("This form is not connected yet — its GoHighLevel destination has not been set. Nothing was sent.", "warn");
        console.warn("[own-a-boxx] data-endpoint is empty; see SETUP-GOHIGHLEVEL-FORM.md");
        return;
      }

      const name = F.name.value.trim();
      const space = name.indexOf(" ");
      const country = COUNTRIES.find((c) => c.value === (selects.dial?.value || "US"));
      const digits = F.phone.value.replace(/\D/g, "");
      const territory = F.territory.value.trim();

      // GHL maps contact fields by key. first_name/last_name go separately as
      // well as whole, because the Create Contact step wants them apart and
      // cannot split one field.
      const payload = {
        first_name: space === -1 ? name : name.slice(0, space),
        last_name: space === -1 ? "" : name.slice(space + 1).trim(),
        full_name: name,
        email: F.email.value.trim(),
        phone: `+${country.dial}${digits}`,       // E.164, so GHL can dial it
        phone_country: country.value,
        territory,
        background: selects.background?.value || "",
        notes: F.notes.value.trim(),
        source: form.dataset.source || "Own a Boxx page",
        page_url: window.location.href,
      };

      sending = true;
      showDone("sending", name, territory);

      try {
        const response = await fetch(endpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        if (!response.ok) throw new Error("HTTP " + response.status);
        // A beat on the spinner: an instant flick from "Sending" to "Thank you"
        // reads as though nothing was sent.
        await new Promise((r) => setTimeout(r, 550));
        done?.setAttribute("data-state", "done");
      } catch (error) {
        console.error("[own-a-boxx] enquiry submit failed", error);
        hideDone();
        say("Something went wrong sending that. Please try again, or email us directly.", "error");
      } finally {
        sending = false;
      }
    }, { signal });

    function showDone(state, name, territory) {
      if (!done) return;
      const first = name.split(" ")[0];
      done.querySelector("[data-done-name]").textContent = first ? `, ${first}` : "";
      const t = done.querySelector("[data-done-territory]");
      if (t) t.textContent = territory || "your market";
      done.setAttribute("data-state", state);
      done.hidden = false;
      wrap?.classList.add("enquire--done");
      wrap?.scrollIntoView({ block: "center", behavior: "smooth" });
    }

    function hideDone() {
      if (!done) return;
      done.hidden = true;
      done.setAttribute("data-state", "sending");
      wrap?.classList.remove("enquire--done");
    }
  }

  /* --------------------------------------------------------------------- */
  /* DEV ONLY — mode preview                                                */
  /* ---------------------------------------------------------------------
     The page ships with data-terms-mode="ranges" and data-cta-mode="form".
     This lets the other variants be reviewed without editing the file:

       index.html?terms=gated
       index.html?cta=calendar&terms=full

     Deliberately NOT ported to the Shopify section. In the theme both
     attributes come from section settings, and a URL parameter that could
     change what a public page discloses about money would be a liability,
     not a convenience.
  --------------------------------------------------------------------- */
  function applyModePreview(root) {
    const params = new URLSearchParams(window.location.search);
    const modes = {
      terms: ["ranges", "full", "gated"],
      cta: ["form", "calendar", "link"],
    };

    Object.entries(modes).forEach(([key, allowed]) => {
      const value = params.get(key);
      if (!value) return;
      if (!allowed.includes(value)) {
        console.warn(
          `[own-a-boxx] ignoring ?${key}=${value}; expected one of ${allowed.join(", ")}`
        );
        return;
      }
      root.setAttribute(`data-${key}-mode`, value);
      console.info(`[own-a-boxx] ${key} mode: ${value}`);
    });
  }

  /* --------------------------------------------------------------------- */
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => init(), { once: true });
  } else {
    init();
  }

  // Shopify theme editor re-renders a section without a page load.
  document.addEventListener("shopify:section:load", (event) => {
    const root = event.target.querySelector?.(ROOT_SELECTOR);
    if (root) init(root);
  });

  window.OwnABoxxPage = { init };
})();
